import React, { useState } from 'react';
import { API_BASE_URL } from '../utils/env';
import { supabase } from '../lib/supabase';

export default function RequestEndorsementModal({ isOpen, onClose, achievement, onSuccess }) {
  const [managerEmail, setManagerEmail] = useState('');
  const [managerName, setManagerName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { success, message }
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  const handleClose = () => {
    setManagerEmail('');
    setManagerName('');
    setResult(null);
    setError(null);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const res = await fetch(`${API_BASE_URL}/endorsements/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          achievement_id: achievement.id,
          manager_email: managerEmail,
          manager_name: managerName || undefined,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send request');
      setResult(data);
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Request Endorsement</h2>
              <p className="text-sm text-gray-500 mt-0.5">Ask your manager to verify this achievement</p>
            </div>
            <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
          </div>

          {/* Achievement preview */}
          <div className="bg-gray-50 rounded-lg p-3 mb-5 border border-gray-200">
            <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">Achievement</p>
            <p className="font-medium text-gray-900 text-sm">{achievement?.title}</p>
            {achievement?.impact && (
              <p className="text-green-700 text-xs mt-1">{achievement.impact}</p>
            )}
          </div>

          {result ? (
            <div className="text-center py-4">
              <div className="text-4xl mb-3">📧</div>
              <h3 className="font-semibold text-gray-900 mb-1">Request sent!</h3>
              <p className="text-sm text-gray-500 mb-4">{result.message}</p>
              <button
                onClick={handleClose}
                className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-700"
              >
                Done
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Manager's email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  required
                  value={managerEmail}
                  onChange={e => setManagerEmail(e.target.value)}
                  placeholder="manager@company.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Manager's name <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={managerName}
                  onChange={e => setManagerName(e.target.value)}
                  placeholder="Jane Smith"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {error}
                </div>
              )}

              <div className="flex gap-3 pt-1">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-700 disabled:opacity-50 transition-colors"
                >
                  {submitting ? 'Sending...' : 'Send endorsement request'}
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2.5 text-gray-500 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>

              <p className="text-xs text-gray-400 text-center">
                Your manager will receive an email with a link — no account needed.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
